import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import { SITE } from "../data/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/works", label: "Works" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];


export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div
          className={`flex items-center justify-between rounded-full px-4 md:px-6 py-3 transition-all duration-500 ${
            scrolled
              ? "glass-strong shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
              : "bg-transparent"
          }`}
        >
          <Link
            to="/"
            className="flex items-center"
            data-testid="logo-link"
          >
            <img
              src="/works/madly-digital-logo-black.png"
              alt={`${SITE.name} logo`}
              className="h-16 md:h-15 w-auto object-contain"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm transition-colors ${
                    isActive ? "text-[#0b2e5e] font-semibold" : "text-[#0b2e5e]/65 hover:text-[#0b2e5e]"
                  }`
                }
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-dot"
                        className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-[#FF3366]"
                        transition={{ duration: 0.4 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link to="/contact" className="btn-primary" data-testid="nav-cta-contact">
              Start a project <ArrowUpRight weight="bold" size={16} />
            </Link>
          </div>

          <button
            className="md:hidden text-[#0b2e5e] p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="md:hidden mt-2 mx-4"
          >
            <div className="glass-strong rounded-3xl p-4 space-y-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-2xl text-base ${
                      isActive ? "bg-[#0b2e5e]/8 text-[#0b2e5e] font-semibold" : "text-[#0b2e5e]/75"
                    }`
                  }
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" className="btn-primary w-full justify-center mt-2" data-testid="mobile-nav-cta">
                Start a project
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
